import type {
  ColDef,
  ColGroupDef,
  ColumnState,
  DefaultMenuItem,
  GridOptions,
  IServerSideDatasource,
  MenuItemDef,
  SideBarDef
} from 'ag-grid-community';
import type { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import type {
  Dispatch,
  ForwardedRef,
  ReactNode,
  RefObject,
  SetStateAction
} from 'react';

export type AgGridRowData = Record<string, unknown>;

export type AgGridColumnDef =
  | ColDef<AgGridRowData>
  | ColGroupDef<AgGridRowData>;

export interface AgGridColumnInput {
  field?: string;
  title?: string;
  type?: string;
  tooltip?: ReactNode;
  icon?: ReactNode;
  gp?: string;
  gptooltip?: ReactNode;
  gpicon?: ReactNode;
  searchable?: boolean;
  noFilter?: boolean;
  suppressRowTransform?: boolean;
  cellRenderer?: unknown;
}

export type AgGridColumn =
  | AgGridColumnInput
  | AgGridColumnDef
  | Record<string, unknown>
  | null;

export type AgGridHeight = string | number;

export interface AgGridColumnStateSettings {
  columns?: ColumnState[];
  filters?: Record<string, unknown> | null;
}

export type AgGridExportRequest = Record<string, unknown>;

export interface AgGridExportResponse {
  data?: {
    data?: unknown;
  };
}

export interface AgGridExportSettings {
  enabled?: boolean;
  fetchFunction?:
    | ((request: AgGridExportRequest) => PromiseLike<unknown>)
    | null;
  additionalData?: Record<string, unknown> | null;
}

export interface AgGridSettings {
  pagination?: boolean;
  fullscreen?: boolean;
  changeHeight?: boolean;
  saveGrid?: boolean;
  resetGrid?: boolean;
  sideBar?: boolean;
  exportBtn?: boolean;
  masterDetail?: unknown;
  columnsState?: AgGridColumnStateSettings;
  additionalData?: Record<string, unknown> | null;
  export?: AgGridExportSettings;
}

export interface AgGridFetchResponse {
  data: {
    rows: AgGridRowData[];
    total: number;
  };
}

export interface AgGridFetchResult {
  unwrap: () => Promise<unknown>;
}

export type AgGridFetchFunction = {
  bivarianceHack(request: Record<string, unknown>): AgGridFetchResult;
}['bivarianceHack'];

export type AgGridMenuItem =
  | DefaultMenuItem
  | MenuItemDef<AgGridRowData>;

export type AgGridRef = RefObject<AgGridReact<AgGridRowData> | null>;

export type AgGridViewRef = RefObject<HTMLDivElement | null>;

export interface AgGridWrapperProps {
  table_name?: string;
  forceDir?: 'rtl' | 'ltr';
  forceFilters?: Record<string, unknown> | null;
  columns?: AgGridColumn[];
  otherButtons?: ReactNode;
  data?: unknown[];
  height?: string;
  otherOptions?: Partial<AgGridReactProps<AgGridRowData>>;
  fetchFunction?: AgGridFetchFunction | null;
  additionalData?: Record<string, unknown>;
  additionalContext?: AgGridMenuItem[];
  hideFooter?: boolean;
  hideExportHelpAlert?: boolean;
  stickyHorizontalScroll?: boolean;
  stickyHeader?: boolean;
  total?: number;
  perPage?: number;
  page_depth?: number;
  settings?: AgGridSettings;
}

export interface AgGridWrapperTrackerProps {
  table_name?: string;
  columns?: AgGridColumn[];
  otherOptions?: Partial<AgGridReactProps<AgGridRowData>>;
  data?: unknown[];
  height?: string;
  fetchFunction?: AgGridFetchFunction | null;
  shouldRevalidateFirstPage?: boolean | null;
  setShouldRevalidateFirstPage?: (value: boolean) => void;
  additionalData?: Record<string, unknown>;
  additionalContext?: AgGridMenuItem[];
  otherButtons?: ReactNode;
  total?: number;
  perPage?: number;
  page_depth?: number;
  settings?: AgGridSettings;
}

export interface AgGridButtonsProps {
  settings: AgGridSettings;
  heightState: AgGridHeight;
  setHeightState: Dispatch<SetStateAction<AgGridHeight>>;
  saveColumnsState: () => Promise<void>;
  resetColumnsState: () => void;
  isSavingState: boolean;
  exportBtn?: boolean;
  heightStateBackUp: RefObject<AgGridHeight | undefined>;
  otherButtons?: ReactNode;
}

export interface AgGridChangeHeightProps {
  heightState: number;
  setHeightState: Dispatch<SetStateAction<AgGridHeight>>;
}

export interface AgGridTreeHandle {
  expandAll: () => void;
  collapseAll: () => void;
}

export interface AgGridTreeRow extends AgGridRowData {
  name: string[];
}

export interface AgGridTreeProps {
  data: AgGridTreeRow[];
  height?: string;
  columnDefs?: (ColDef<AgGridTreeRow> | ColGroupDef<AgGridTreeRow>)[];
  autoGroupColumnDef?: ColDef<AgGridTreeRow>;
  autoSizeOnReady?: boolean;
  fitColumns?: boolean;
  settings?: Pick<AgGridSettings, 'fullscreen' | 'changeHeight'>;
}

export interface AgGridTreeGroupsProps {
  perPage?: number;
  height?: string;
  data?: AgGridRowData[];
  settings?: AgGridSettings;
}

export interface AgGridMasterDetailItem extends AgGridRowData {
  additionalData?: Record<string, unknown>;
}

export interface AgGridMasterDetailsParams extends AgGridRowData {
  targetParam?: string;
}

export interface AgGridLoadingParams {
  loadingMessage?: string;
  node: {
    failedLoad?: boolean;
  };
}

export function assignForwardedRef<T>(
  ref: ForwardedRef<T>,
  value: T | null
): void {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

export type AgGridTransactionRef<TData = AgGridRowData> = RefObject<
  AgGridReact<TData> | null
>;

export interface AgGridTransactionRow extends AgGridRowData {
  id: string | number;
}

export type AgGridGridOptions<TData = AgGridRowData> = GridOptions<TData>;

export type AgGridServerSideDatasource = IServerSideDatasource<AgGridRowData>;

export type AgGridSidebarConfig = {
  sideBar: SideBarDef;
};

declare global {
  interface Window {
    colState: ColumnState[];
  }
}
